import {
  OfflineMapService,
  OfflineMapStateManager,
} from '@onxmaps/x-offline-maps'
import type { OfflineMapServiceDependencies } from '@onxmaps/x-offline-maps'
import { offlineServiceState } from '@/services/offline-map-service/'
import {
  transformOfflineMapsPayload,
  transformOfflineRegionsOrRegionToGeoJson,
} from '@/services/offline-map-service/helpers'

let offlineMapService: OfflineMapService | null = null
let offlineMapStateManager: OfflineMapStateManager | null = null

const deps: OfflineMapServiceDependencies = {
  transforms: {
    transformOfflineMapsPayload,
    transformOfflineRegionsOrRegionToGeoJson,
  },
}

/**
 * Registers event listeners on the state manager that keep the existing Vue
 * reactive `offlineServiceState` in sync with the package's EventEmitter-based
 * state. This bridges the framework-agnostic package into the app's existing
 * Vue reactivity system so components can be migrated incrementally without
 * requiring changes to how they consume state.
 */
const bridgeStateManagerToReactiveState = (
  stateManager: OfflineMapStateManager
): void => {
  stateManager.on('maps-loaded', ({ maps }) => {
    offlineServiceState.hasRequested = true
    offlineServiceState.maps = maps
  })

  stateManager.on('map-created', ({ map }) => {
    offlineServiceState.maps?.push(map)
  })

  stateManager.on('map-updated', ({ map }) => {
    if (!offlineServiceState.maps) {
      return
    }
    const index = offlineServiceState.maps.findIndex((m) => m.uuid === map.uuid)
    if (index !== -1) {
      offlineServiceState.maps[index] = map
    }
  })

  stateManager.on('map-deleted', ({ uuid }) => {
    if (!offlineServiceState.maps) {
      return
    }
    const index = offlineServiceState.maps.findIndex((m) => m.uuid === uuid)
    if (index !== -1) {
      offlineServiceState.maps.splice(index, 1)
    }
  })

  stateManager.on('draft-started', ({ draftMap }) => {
    Object.assign(offlineServiceState.draftMap, draftMap)
  })

  stateManager.on('draft-updated', ({ draftMap }) => {
    Object.assign(offlineServiceState.draftMap, draftMap)
  })

  stateManager.on('draft-ended', () => {
    offlineServiceState.draftMap = {}
  })

  stateManager.on('create-mode-changed', ({ inCreateMode }) => {
    offlineServiceState.inCreateMode = inCreateMode
  })

  stateManager.on('estimate-updated', ({ estimatedSize }) => {
    offlineServiceState.draftMap.estimatedSize = estimatedSize
  })

  stateManager.on('max-area-updated', ({ maxArea }) => {
    offlineServiceState.draftMap.maxArea = maxArea
  })
}

/**
 * Creates the OfflineMapStateManager and OfflineMapService singletons and
 * connects the state manager's events to the app's Vue reactive state.
 *
 * Must be called after `configureOfflineMaps()` has been invoked, since the
 * service relies on the global config for its API client.
 *
 * No-ops if already initialized to prevent duplicate event listener registration.
 */
export const initializeOfflineMapServiceInstance = (): void => {
  if (offlineMapService !== null) {
    return
  }

  offlineMapStateManager = new OfflineMapStateManager()
  offlineMapService = new OfflineMapService(offlineMapStateManager, deps)
  bridgeStateManagerToReactiveState(offlineMapStateManager)
  offlineServiceState.serviceInitialized = true
}

/**
 * Returns the initialized OfflineMapService singleton.
 *
 * @throws Error if called before `initializeOfflineMapServiceInstance()`
 */
export const getOfflineMapService = (): OfflineMapService => {
  if (offlineMapService === null) {
    throw new Error(
      '[OfflineMapService] Service not initialized. Ensure initializeXOfflineMapsWhenEnabled() has been called.'
    )
  }
  return offlineMapService
}

/**
 * Returns the initialized OfflineMapStateManager singleton.
 *
 * @throws Error if called before `initializeOfflineMapServiceInstance()`
 */
export const getOfflineMapStateManager = (): OfflineMapStateManager => {
  if (offlineMapStateManager === null) {
    throw new Error(
      '[OfflineMapService] State manager not initialized. Ensure initializeXOfflineMapsWhenEnabled() has been called.'
    )
  }
  return offlineMapStateManager
}
